// TeacherDayPlannerSelf.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';
import { Building2 } from 'lucide-react';
import { FaBan, FaLock, FaTrash } from 'react-icons/fa';

/** ---------- Time utils ---------- */
function normalizeTime(val) {
  if (!val) return '';
  let t = String(val);
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}
function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
}
function addMinutes(time, mins) { return minutesToHHMM(toMinutes(time) + mins); }
function clamp(v, min, max) {
  const n = Number(v);
  const x = Number.isFinite(n) ? n : min;
  return Math.max(min, Math.min(max, x));
}
function clampTo(i, startM, endM) {
  const f = Math.max(toMinutes(i.from), startM);
  const t = Math.min(toMinutes(i.to), endM);
  if (t <= f) return null;
  return { from: minutesToHHMM(f), to: minutesToHHMM(t) };
}
function simplifyIntervals(list = []) {
  return (list || []).map(x => ({
    from: normalizeTime(x.from_time ?? x.from),
    to: normalizeTime(x.to_time ?? x.to),
  })).filter(i => toMinutes(i.to) > toMinutes(i.from));
}
function mergeTouching(intervals = []) {
  const sorted = intervals.slice().sort((x,y)=>toMinutes(x.from)-toMinutes(y.from));
  const out = [];
  for (const it of sorted) {
    if (!out.length) out.push({...it});
    else {
      const last = out[out.length-1];
      if (toMinutes(it.from) <= toMinutes(last.to)) {
        if (toMinutes(it.to) > toMinutes(last.to)) last.to = it.to;
      } else out.push({...it});
    }
  }
  return out;
}
function subtractIntervals(base = [], blocked = []) {
  let result = simplifyIntervals(base);
  const bad = simplifyIntervals(blocked);
  for (const exc of bad) {
    const excFrom = toMinutes(exc.from), excTo = toMinutes(exc.to);
    const next = [];
    for (const it of result) {
      const a = toMinutes(it.from), b = toMinutes(it.to);
      if (excTo <= a || excFrom >= b) next.push(it);
      else {
        if (excFrom > a) next.push({ from: it.from, to: normalizeTime(exc.from) });
        if (excTo   < b) next.push({ from: normalizeTime(exc.to), to: it.to });
      }
    }
    result = next;
  }
  return result;
}
function intersectTwoSets(A = [], B = []) {
  const res = [];
  const a = simplifyIntervals(A);
  const b = simplifyIntervals(B);
  for (const i of a) {
    for (const j of b) {
      const from = Math.max(toMinutes(i.from), toMinutes(j.from));
      const to   = Math.min(toMinutes(i.to),   toMinutes(j.to));
      if (to > from) res.push({ from: minutesToHHMM(from), to: minutesToHHMM(to) });
    }
  }
  return mergeTouching(res);
}

/** ---------- Timeline config ---------- */
const SNAP_MIN  = 5;
/**
 * Legacy general slot-min (fallback),
 * ali sada discipline + telehealth diktiraju trajanja.
 */
const SLOT_MIN  = 45;

/** ---------- Service duration rules (from system_settings) ---------- */
// global cache so helpers below can read latest settings without refactoring whole file
let SERVICE_DURATION_RULES = null;

// map PROG_* -> settings key
function keyFromDiscipline(disciplineCode) {
  if (!disciplineCode) return 'generic';
  const s = String(disciplineCode || '');
  if (s.startsWith('PROG_')) return s.slice(5); // EI, PI, ABA, OT, PT, ST
  return s;
}

function numOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// turn one rule into {base,min,max} or null
function cfgFromRule(rule) {
  if (!rule || typeof rule !== 'object') return null;
  if (rule.type === 'fixed') {
    const f = numOrNull(rule.fixed);
    if (f == null) return null;
    return { base: f, min: f, max: f };
  }
  if (rule.type === 'range') {
    const base = numOrNull(rule.default) ?? numOrNull(rule.base);
    const min  = numOrNull(rule.min);
    const max  = numOrNull(rule.max);
    if (base == null && min == null && max == null) return null;

    // be safe with fallbacks
    const b = base ?? min ?? 45;
    const mn = min ?? Math.min(b, 45);
    const mx = max ?? Math.max(b, 90);
    return { base: b, min: mn, max: mx };
  }
  return null;
}

// pretty text for UI
function formatRuleLine(label, rule, fallbackText) {
  const cfg = cfgFromRule(rule);
  if (!cfg) return fallbackText;
  if (cfg.min === cfg.max) return `${label} = ${cfg.base} min (fixed)`;
  return `${label} = ${cfg.base} default (${cfg.min}–${cfg.max})`;
}

function getDurationConfig(disciplineCode, _typeIgnored, deliveryMode = 'in_person') {
  // Telehealth override – nezavisno od discipline (admin-style has TELEHEALTH in settings; keep FE-safe)
  if (deliveryMode === 'telehealth') {
    const thRule = SERVICE_DURATION_RULES && SERVICE_DURATION_RULES.TELEHEALTH;
    const thCfg = cfgFromRule(thRule);
    return thCfg || { base: 60, min: 45, max: 90 };
  }

  const key = keyFromDiscipline(disciplineCode);
  const rule = SERVICE_DURATION_RULES && SERVICE_DURATION_RULES[key];

  const fromSettings = cfgFromRule(rule);
  if (fromSettings) return fromSettings;

  // ---- FALLBACK ----
  switch (disciplineCode) {
    case 'PROG_EI':
      return { base: 45, min: 45, max: 45 };
    case 'PROG_PI':
      return { base: 90, min: 90, max: 90 };
    case 'PROG_ABA':
      return { base: 90, min: 45, max: 240 };
    case 'PROG_OT':
    case 'PROG_PT':
      return { base: 45, min: 45, max: 90 };
    case 'PROG_ST':
      return { base: 45, min: 45, max: 45 };
    default:
      return { base: 45, min: 45, max: 90 };
  }
}

/** Vrati osnovnu dužinu u minutima */
function getDisciplineDuration(disciplineCode, type, deliveryMode = 'in_person') {
  return getDurationConfig(disciplineCode, type, deliveryMode).base;
}

/** Hard-limit (fixed) in-person durations */
function isDisciplineHardLimited(disciplineCode) {
  const cfg = getDurationConfig(disciplineCode, 'standard', 'in_person');
  return cfg.min === cfg.max;
}

/** Min/max duration */
function getMinMaxDuration(disciplineCode, type, deliveryMode = 'in_person') {
  const cfg = getDurationConfig(disciplineCode, type, deliveryMode);
  return { min: cfg.min, max: cfg.max };
}

/** ---------- UI helpers ---------- */
function ReassignedBadge({ value }) {
  if (!value || value === 'none') return null;
  const cls = {
    rejected: 'bg-red-100 text-red-700',
    pending:  'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-800',
  }[value] || 'bg-gray-100 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{value}</span>;
}

const COLOR_PALETTE = [
  'bg-emerald-400', 'bg-sky-400', 'bg-fuchsia-400',
  'bg-amber-400', 'bg-lime-400', 'bg-rose-400',
  'bg-cyan-400', 'bg-violet-400', 'bg-orange-400'
];
function colorForId(id) {
  if (!id && id !== 0) return 'bg-gray-400';
  const idx = Math.abs(parseInt(id, 10)) % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx];
}
function initials(name='') {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0,2).map(p=>p[0]?.toUpperCase()||'').join('');
}

// Telehealth pill/ikona
function TelehealthPill({ mode }) {
  if (mode !== 'telehealth') return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
      <span className="text-xs">📹</span>
      <span>Telehealth</span>
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-200 text-green-900',
    canceled: 'bg-purple-200 text-purple-700',
  };
  const cls = map[status] || 'bg-gray-200 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status || '—'}</span>;
}
function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
}

// ----- Distance + prev-ref -----
const fmtMi  = v => (v==null ? null : `${Number(v).toFixed(2)} mi`);
const fmtMin = v => (v==null ? null : `${Math.round(Number(v))} min`);

function DistanceLine({ item }) {
  const rmi = item?.route_mi;
  const rmn = item?.route_min;
  const sli = item?.straight_line_mi;
  const src = item?.location_source;

  const parts = [];
  if (rmi != null && rmn != null) parts.push(`${fmtMi(rmi)} (${fmtMin(rmn)})`);
  else if (rmi != null) parts.push(fmtMi(rmi));
  if (sli != null) parts.push(`straight ${fmtMi(sli)}`);
  if (src === 'alt') parts.push('ALT address');
  if (src === 'primary') parts.push('Primary address');

  if (!parts.length) return null;
  return <span className="text-xs text-gray-600">{parts.join(' • ')}</span>;
}

function PrevRefPill({ prev_ref }) {
  if (!prev_ref?.end_time) return null;
  return (
    <span className="text-[11px] text-gray-500 ml-2">
      from last stop {prev_ref.end_time}
    </span>
  );
}

/** ---------- Disciplines (taxonomy PROG_*) ---------- */
const DISCIPLINE_BUTTONS = [
  { code: 'PROG_EI',  short: 'EI',  label: 'Early Intervention',   chipClass: 'bg-blue-100 text-blue-700' },
  { code: 'PROG_PI',  short: 'PI',  label: 'Project Impact',       chipClass: 'bg-orange-100 text-orange-700' },
  { code: 'PROG_ABA', short: 'ABA', label: 'ABA',                  chipClass: 'bg-purple-100 text-purple-700' },
  { code: 'PROG_OT',  short: 'OT',  label: 'Occupational Therapy', chipClass: 'bg-teal-100 text-teal-700' },
  { code: 'PROG_PT',  short: 'PT',  label: 'Physical Therapy',     chipClass: 'bg-lime-100 text-lime-700' },
  { code: 'PROG_ST',  short: 'ST',  label: 'Speech Therapy',       chipClass: 'bg-green-100 text-green-700' },
];

function getDisciplineMeta(code) {
  return DISCIPLINE_BUTTONS.find(d => d.code === code) || null;
}

function DisciplineBadge({ code }) {
  if (!code) return null;
  const meta = getDisciplineMeta(code);
  if (!meta) return null;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.chipClass}`}>
      {meta.short}
    </span>
  );
}

/**
 * Discipline buttons per student.
 * Klik na disciplinu SAMO setuje izabranu disciplinu (ne pravi vizitu).
 */
function DisciplineButtonsRow({ student, isEligible = true, onSelectDiscipline, selectedCode }) {
  const programs = Array.isArray(student.program_codes)
    ? student.program_codes
    : Array.isArray(student.disciplines)
      ? student.disciplines
      : [];

  const taken = new Set(
    Array.isArray(student.taken_program_codes)
      ? student.taken_program_codes
      : []
  );

  const hasPrograms = programs.length > 0;
  if (!hasPrograms) return null;

  return (
    <div className="flex flex-wrap gap-1 justify-end">
      {DISCIPLINE_BUTTONS.map(def => {
        const hasDisc = programs.includes(def.code);
        const already = taken.has(def.code);
        const disabled = !isEligible || !hasDisc || already;

        let tooltip = '';
        if (!isEligible) tooltip = 'Not eligible for this time-slot (server rule).';
        else if (!hasDisc) tooltip = 'Discipline not assigned for this student.';
        else if (already) tooltip = 'This student already has a visit for this discipline on this day.';
        else tooltip = 'Select this discipline.';

        const isSelected = !disabled && selectedCode === def.code;

        return (
          <button
            key={def.code}
            type="button"
            disabled={disabled}
            title={tooltip}
            onClick={() => !disabled && onSelectDiscipline(def.code)}
            className={
              'px-2 py-0.5 rounded text-xs border transition ' +
              (disabled
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : isSelected
                  ? `${def.chipClass} border-blue-500 ring-1 ring-blue-400`
                  : `${def.chipClass} border-transparent hover:opacity-90`)
            }
          >
            {def.short}
          </button>
        );
      })}
    </div>
  );
}

/** ---------- Work + clinic helpers (matching TeacherDayPlanner) ---------- */
function isWorkBlock(v) {
  if (!v) return false;
  const kind = String(v.kind || '').toLowerCase();
  const type = String(v.type || '').toLowerCase();
  const busyAs = String(v.busy_as || '').toLowerCase();
  return kind === 'work' || type === 'work' || busyAs === 'work';
}
function isWorkCandidate(c) {
  if (!c) return false;
  const ck = String(c.candidate_kind || '').toLowerCase();
  if (ck === 'work') return true;
  if (!c.student && c.work_type) return true;
  return false;
}
function getWorkId(v) {
  if (!v) return null;
  if (v.work_id) return v.work_id;
  if (typeof v.id === 'string' && v.id.startsWith('work_')) return v.id.slice(5);
  return v.id || null;
}
function getVisitId(v) {
  return v?.visit_id || v?.id || null;
}

/** Cancel modal constants (matching TeacherDayPlanner) */
const CANCEL_REASONS = [
  'Cancelled by teacher',
  'Cancelled by family',
  'Late cancellation',
  'NoShow/withAppointmentConfirmation',
];
const WORK_CANCEL_QUICK_REASONS = [
  'Schedule changed — work task moved to another time.',
  'Task no longer needed — completed elsewhere.',
  'Provider unavailable — will reschedule.',
];

/** ---------- Component za teachera (self) ---------- */
export default function TeacherDayPlannerSelf() {
  // Ulogovani teacher
  const [currentTeacherId, setCurrentTeacherId] = useState('');
  const [currentTeacherName, setCurrentTeacherName] = useState('');

  // Global lists
  const [students, setStudents] = useState([]);

  // Selection
  const [selectedStudentId, setSelectedStudentId] = useState(''); // optional overlap hint

  // Calendar / week
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  // Server time
  const [serverDateStr, setServerDateStr] = useState('');
  const [serverTimeHM, setServerTimeHM] = useState('00:00');

  // Availability caches
  const [teacherAvailByDate, setTeacherAvailByDate] = useState({});
  const [teacherBusyByDate, setTeacherBusyByDate] = useState({});

  const [studentAvailCache, setStudentAvailCache] = useState({});
  const [studentVisitsCache, setStudentVisitsCache] = useState({});

  // Local candidates (unsaved)
  const [candidatesByDate, setCandidatesByDate] = useState({});

  // Modal anchor (teacher free segment)
  const [modalAnchor, setModalAnchor] = useState({ from: '', to: '' });

  // UI state
  const [status, setStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalDate, setModalDate] = useState('');
  const [modalFrom, setModalFrom] = useState('');
  const [modalMaxTo, setModalMaxTo] = useState('');
  const [eligibleStudents, setEligibleStudents] = useState([]); // [{student, ok, reason}]
  const [otherEligible, setOtherEligible] = useState([]);
  const [loadingOther, setLoadingOther] = useState(false);
  const fetchRef = useRef(0);

  // Distance controls
  const [distanceMode, setDistanceMode] = useState('haversine'); // 'none' | 'haversine' | 'ors'
  const [sortOthersByDistance, setSortOthersByDistance] = useState(false);

  // Drag
  const [drag, setDrag] = useState(null);
  const trackRefs = useRef({});

  // Dynamic workday
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });

  // Telehealth modal state
  const [showTeleModal, setShowTeleModal] = useState(false);
  const [teleStudent, setTeleStudent] = useState(null);
  const [teleLink, setTeleLink] = useState('');
  const [teleError, setTeleError] = useState('');
  const [teleDisciplineCode, setTeleDisciplineCode] = useState(null);

  // Izabrane discipline po studentu (za modal)
  const [selectedDisciplines, setSelectedDisciplines] = useState({});

  // Service duration settings
  const [serviceDurationRules, setServiceDurationRules] = useState(null);

  /** ---------- Clinic + org locations (matching TeacherDayPlanner) ---------- */
  const [visitLocationMode, setVisitLocationMode] = useState('at_home'); // at_home | at_clinic
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinicModalOpen, setClinicModalOpen] = useState(false);

  const [orgLocations, setOrgLocations] = useState([]);
  const [orgLocationsLoading, setOrgLocationsLoading] = useState(false);

  /** ---------- Work candidate modal ---------- */
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [workSubmitting, setWorkSubmitting] = useState(false);
  const [workForm, setWorkForm] = useState({
    location_value: '',
    work_type: 'admin',
    planned_minutes: 60,
    title: '',
    notes: '',
  });

  /** ---------- Cancel modal (visit/work) ---------- */
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null); // { date, item }
  const [cancelMode, setCancelMode] = useState('visit');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // Učitaj ulogovanog teachera iz localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u && u.id) {
          setCurrentTeacherId(String(u.id));
          setCurrentTeacherName(u.name || '');
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Workday settings
  useEffect(() => {
    API.get('/workday-settings')
      .then(res => setWorkday({
        start: (res.data?.start || '08:00').slice(0,5),
        end:   (res.data?.end   || '18:00').slice(0,5),
      }))
      .catch(()=>{});
  }, []);

  // Derived from workday
  const DAY_START = workday.start;
  const DAY_END   = workday.end;
  const dayStartM = toMinutes(DAY_START);
  const dayEndM   = toMinutes(DAY_END);
  const spanM     = Math.max(1, dayEndM - dayStartM);

  const durationLegendText = useMemo(() => {
    const rules = serviceDurationRules || null;

    const ei   = formatRuleLine('EI',    rules?.EI,    'EI = 45 min (fixed)');
    const st   = formatRuleLine('ST',    rules?.ST,    'ST = 45 min (fixed)');
    const pi   = formatRuleLine('PI',    rules?.PI,    'PI = 90 min (fixed)');
    const aba  = formatRuleLine('ABA',   rules?.ABA,   'ABA = 90 default (45–240)');
    const ot   = formatRuleLine('OT/PT', rules?.OT,    'OT/PT = 45 default (45–90)');
    const th = 'Telehealth = 60 default (45–90)';

    return `${ei} • ${st} • ${pi} • ${aba} • ${ot} • ${th}`;
  }, [serviceDurationRules]);

  const durationLegendSpans = useMemo(() => {
    const parts = durationLegendText.split(' • ');
    return parts.map((t, i) => <span key={i}>{t}</span>);
  }, [durationLegendText]);

  const pctBox = (i) => {
    const left = ((toMinutes(i.from) - dayStartM) / spanM) * 100;
    const width = ((toMinutes(i.to) - toMinutes(i.from)) / spanM) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(0, Math.min(100 - left, width))}%`
    };
  };

  // Toast auto-clear
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(''), 3000);
    return () => clearTimeout(t);
  }, [status]);

  /** ---------- Org locations ---------- */
  async function loadOrgLocations() {
    if (orgLocationsLoading) return;
    setOrgLocationsLoading(true);
    try {
      const res = await API.get('/org-locations');
      const locs = Array.isArray(res.data?.locations) ? res.data.locations : [];
      setOrgLocations(locs);

      const defId = res.data?.clinic_settings?.default_location_id;
      const hasDef = defId && locs.some(l => String(l.id) === String(defId));
      setWorkForm(prev => {
        if (prev.location_value) return prev;
        if (hasDef) return { ...prev, location_value: `org:${defId}` };
        if (locs.length) return { ...prev, location_value: `org:${locs[0].id}` };
        return prev;
      });
    } catch (e) {
      console.error('loadOrgLocations failed:', e);
      setOrgLocations([]);
    } finally {
      setOrgLocationsLoading(false);
    }
  }

  function resolveClinicLabel(orgLocationId) {
    if (!orgLocationId) return null;
    const found = (orgLocations || []).find(l => String(l.id) === String(orgLocationId));
    if (!found) return `Clinic #${orgLocationId}`;
    const name = found.name || `Clinic #${orgLocationId}`;
    const extra = found.geocoded_label ? ` — ${found.geocoded_label}` : '';
    return `${name}${extra}`;
  }

  function isClinicLikeItem(raw, isWork) {
    const lm = String(raw?.location_mode || '').toLowerCase();
    const oid = raw?.org_location_id ? Number(raw.org_location_id) : null;
    if (!oid) return false;

    if (!isWork && lm === 'at_clinic') return true;
    if (isWork && lm === 'org_location') return true;
    return false;
  }

  useEffect(() => {
    let needs = false;

    for (const date of Object.keys(teacherBusyByDate || {})) {
      const busy = teacherBusyByDate[date] || [];
      for (const v of busy) {
        const isWork = isWorkBlock(v);
        if (isClinicLikeItem(v, isWork)) { needs = true; break; }
      }
      if (needs) break;
    }

    if (!needs) {
      for (const date of Object.keys(candidatesByDate || {})) {
        const list = candidatesByDate[date] || [];
        for (const c of list) {
          const isWork = isWorkCandidate(c);
          if (isClinicLikeItem(c, isWork)) { needs = true; break; }
        }
        if (needs) break;
      }
    }

    if (needs && !orgLocationsLoading && (!orgLocations || orgLocations.length === 0)) {
      loadOrgLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherBusyByDate, candidatesByDate]);

  function openClinicPicker() {
    if (!orgLocations.length) loadOrgLocations();
    setClinicModalOpen(true);
  }

  function setAtHomeMode() {
    setVisitLocationMode('at_home');
    setSelectedClinic(null);
  }

  /** ---------- Initial loads ---------- */
  useEffect(() => {
    API.get('/server-time').then(res => {
      const s = res.data?.serverTime || '';
      setServerDateStr(s.slice(0,10));
      setServerTimeHM(s.slice(11,16));
    }).catch(()=>{});

    API.get('/service-duration-settings')
      .then(res => {
        const rules = res.data || null;
        setServiceDurationRules(rules);
        SERVICE_DURATION_RULES = rules;
      })
      .catch(() => {
        setServiceDurationRules(null);
        SERVICE_DURATION_RULES = null;
      });

    API.get('/admin/students?assigned_only=0')
      .then(res => setStudents(res.data || []))
      .catch(()=>{});

    API.get('/weeks')
      .then(res => setWeekOptions(res.data || []))
      .catch(()=>{});
  }, []);

  useEffect(() => {
    if (!selectedWeekStart) return;
    API.get(`/week-dates/${selectedWeekStart}`)
      .then(res => setWeekDates(res.data || []))
      .catch(()=>{});
  }, [selectedWeekStart]);

  // Default students za ulogovanog teachera
  const defaultStudents = useMemo(() => {
    if (!currentTeacherId) return [];
    return (students || []).filter(
      s => String(s.default_teacher_id) === String(currentTeacherId)
    );
  }, [currentTeacherId, students]);

  // Reset caches na promenu week
  useEffect(() => {
    setTeacherAvailByDate({});
    setTeacherBusyByDate({});
    setCandidatesByDate({});
    setSelectedStudentId('');
  }, [currentTeacherId, selectedWeekStart]);

  // Helpers
  const getStudentFull = (id) =>
    (students || []).find(s => Number(s.id) === Number(id));
  const getStudentDefaultTid = (id) =>
    getStudentFull(id)?.default_teacher_id ?? null;

  /** Past handling */
  function isPastDate(date) { return date < serverDateStr; }

  /** ---------- Load teacher availability + busy visits + busy work ---------- */
  useEffect(() => {
    if (!currentTeacherId || !selectedWeekStart) return;

    (async () => {
      try {
        const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${currentTeacherId}`);
        const a = res.data?.availability || {};
        const e = res.data?.exceptions || {};
        const daysOff = res.data?.day_offs || [];

        const base = {};
        Object.keys(a).forEach(date => {
          const isDayOff = daysOff.includes(date);
          const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
          base[date] = { free };
        });

        const b = await API.get('/visits/teacher-week-all', {
          params: { teacher_id: currentTeacherId, week_start: selectedWeekStart }
        });
        const busy = {};
        (Object.entries(b.data || {})).forEach(([date, list]) => {
          busy[date] = (list || []).map(r => ({
            id: r.id,
            from: normalizeTime(r.from_time),
            to: normalizeTime(r.to_hhmm || r.to_time || r.to),
            student_id: r.student_id,
            student_name: r.student_name,
            type: r.type || 'standard',
            status: r.status || 'draft',
            reassigned_status: r.reassigned_status || 'none',
            discipline_code: r.discipline_code || null,
            delivery_mode: r.delivery_mode || 'in_person',
            telehealth_link: r.telehealth_link || null,
            location_mode: r.location_mode || null,
            org_location_id: r.org_location_id || null,
          }));
        });

        try {
          const w = await API.get('/teacher-week-planner', {
            params: { teacher_id: currentTeacherId, week_start: selectedWeekStart }
          });
          const byDate = w.data?.byDate || {};
          Object.keys(byDate).forEach(date => {
            const dayBusy = Array.isArray(byDate?.[date]?.busy) ? byDate[date].busy : [];
            const workItems = dayBusy.filter(x => isWorkBlock(x) || String(x.busy_as || '') === 'work' || String(x.type || '') === 'work');
            if (!workItems.length) return;
            if (!busy[date]) busy[date] = [];
            workItems.forEach(r => {
              busy[date].push({
                id: `work_${r.work_id || getWorkId(r) || r.id}`,
                kind: 'work',
                work_id: r.work_id || getWorkId(r) || null,
                from: normalizeTime(r.from),
                to: normalizeTime(r.to),
                student_id: null,
                student_name: r.title || r.student_name || r.work_type || 'Work',
                type: 'work',
                status: r.status || 'draft',
                reassigned_status: 'none',
                discipline_code: null,
                delivery_mode: null,
                telehealth_link: null,
                busy_as: 'work',
                work_type: r.work_type || 'other',
                title: r.title || null,
                notes: r.notes || null,
                location_mode: r.location_mode || null,
                org_location_id: r.org_location_id || null,
              });
            });
          });
        } catch {
          // ignore
        }

        Object.keys(base).forEach(date => {
          const free = base[date].free || [];
          const blocked = (busy[date] || [])
            .filter(x => x.status !== 'canceled')
            .map(x => ({ from: x.from, to: x.to }));
          base[date].free = subtractIntervals(free, blocked);
        });

        setTeacherBusyByDate(busy);
        setTeacherAvailByDate(base);
      } catch {
        setStatus('Failed to load availability.');
      }
    })();
  }, [currentTeacherId, selectedWeekStart]);

  /** ---------- Lazy-load student availability + visits for week ---------- */
  async function ensureStudentWeek(studentId) {
    if (!studentId || !selectedWeekStart) {
      return { freeByDate: {}, visitsByDate: {} };
    }

    const cache = studentAvailCache[studentId];
    const visitsCache = studentVisitsCache[studentId];
    const hasAvail = cache && cache.__week === selectedWeekStart;
    const hasVisits = visitsCache && visitsCache.__week === selectedWeekStart;

    let freeByDate = hasAvail ? cache.freeByDate : null;
    let visitsByDate = hasVisits ? visitsCache.visitsByDate : null;

    try {
      if (!hasAvail) {
        const res = await API.get(`/student-availability/realweek/${studentId}/${selectedWeekStart}`);
        const map = {};
        Object.entries(res.data || {}).forEach(([date, obj]) => {
          const intervals = simplifyIntervals(obj.intervals || []);
          const blocked   = simplifyIntervals(obj.blocked || []);
          map[date] = subtractIntervals(intervals, blocked);
        });
        freeByDate = map;
        setStudentAvailCache(prev => ({
          ...prev,
          [studentId]: { __week: selectedWeekStart, freeByDate: map }
        }));
      }
    } catch {
      freeByDate = freeByDate || {};
    }

    try {
      if (!hasVisits) {
        const res2 = await API.get(`/visits/week/${studentId}/${selectedWeekStart}`);
        const map2 = {};
        (Object.entries(res2.data || {})).forEach(([date, list]) => {
          map2[date] = (list || []).map(s => ({
            from: normalizeTime(s.from),
            to: normalizeTime(s.to),
            visit_id: s.visit_id,
            type: s.type || 'standard',
            discipline_code: s.discipline_code || null,
            delivery_mode: s.delivery_mode || 'in_person',
            telehealth_link: s.telehealth_link || null,
            status: s.status || 'draft',
            location_mode: s.location_mode || null,
            org_location_id: s.org_location_id || null,
          }));
        });
        visitsByDate = map2;
        setStudentVisitsCache(prev => ({
          ...prev,
          [studentId]: { __week: selectedWeekStart, visitsByDate: map2 }
        }));
      }
    } catch {
      visitsByDate = visitsByDate || {};
    }

    return {
      freeByDate: freeByDate || {},
      visitsByDate: visitsByDate || {}
    };
  }

  /** ---------- Intersection hint (teacher & jedan default student) ---------- */
  const intersectionByDate = useMemo(() => {
    if (!selectedStudentId) return {};
    const sCache = studentAvailCache[selectedStudentId];
    if (!sCache || sCache.__week !== selectedWeekStart) return {};
    const map = {};
    for (const { date } of weekDates) {
      const teacherFree = (teacherAvailByDate[date]?.free || [])
        .map(i => clampTo(i, dayStartM, dayEndM))
        .filter(Boolean);
      const studentFree = (sCache.freeByDate?.[date] || [])
        .map(i => clampTo(i, dayStartM, dayEndM))
        .filter(Boolean);
      map[date] = intersectTwoSets(teacherFree, studentFree);
    }
    return map;
  }, [selectedStudentId, teacherAvailByDate, studentAvailCache, weekDates, selectedWeekStart, dayStartM, dayEndM]);

  /**
   * ---- Per-day per-discipline limit helper: 1 visit per discipline per student per day ----
   */
  function canAddVisitForStudent(date, student, programCode) {
    if (!student || !student.id) return false;
    const studentId = student.id;
    const disc = programCode || null;

    const dayCandidates = candidatesByDate[date] || [];

    // Ne dozvoli drugi kandidat sa istom disciplinom istog dana
    const hasLocalSameDisc = dayCandidates.some(v =>
      v.student?.id === studentId &&
      (disc || null) === (v.discipline_code || null)
    );
    if (hasLocalSameDisc) return false;

    if (disc) {
      const taken = Array.isArray(student.taken_program_codes)
        ? student.taken_program_codes
        : [];
      if (taken.includes(disc)) return false;

      const vc = studentVisitsCache[studentId];
      if (vc && vc.__week === selectedWeekStart) {
        const visitsDay = vc.visitsByDate?.[date] || [];
        const hasSameDisc = visitsDay.some(v => v.discipline_code === disc && v.status !== 'canceled');
        if (hasSameDisc) return false;
      }

      return true;
    }

    // Generic fallback (rare)
    const localAny = dayCandidates.some(v => v.student?.id === studentId && !isWorkCandidate(v));
    if (localAny) return false;

    const vc = studentVisitsCache[studentId];
    const hasExisting =
      vc &&
      vc.__week === selectedWeekStart &&
      Array.isArray(vc.visitsByDate?.[date]) &&
      vc.visitsByDate[date].some(v => v.status !== 'canceled');

    return !hasExisting;
  }

  /** ---------- Helper: da li from–to staje između postojećih (bez preklapanja) ---------- */
  function fitsInFreeWindow(date, anchor, from, to, excludeCandidateIndex = null) {
    const busySaved = (teacherBusyByDate[date] || [])
      .filter(v => v.status !== 'canceled')
      .map(v => ({ from: v.from, to: v.to }));

    const busyCandidates = (candidatesByDate[date] || [])
      .filter((_, idx) => idx !== excludeCandidateIndex)
      .map(c => ({ from: c.from, to: c.to }));

    const blocked = [...busySaved, ...busyCandidates];

    const anchorArr = anchor
      ? [{ from: anchor.from, to: anchor.to }]
      : [{ from: DAY_START, to: DAY_END }];

    const free = subtractIntervals(anchorArr, blocked);

    const fMin = toMinutes(from);
    const tMin = toMinutes(to);

    return free.some(seg =>
      fMin >= toMinutes(seg.from) &&
      tMin <= toMinutes(seg.to)
    );
  }

  /** ---------- Origin override from UNSAVED candidates (clinic-only) ---------- */
  function buildOriginOverrideForModalInterval(dateStr, fromHHMM) {
    try {
      const d = String(dateStr || '').slice(0, 10);
      const fromM = toMinutes(fromHHMM);
      const list = Array.isArray(candidatesByDate?.[d]) ? candidatesByDate[d] : [];
      if (!list.length) return null;

      let best = null;
      let bestToM = -1;

      for (const c of list) {
        if (!c) continue;
        if (isWorkCandidate(c)) continue;

        const toM = toMinutes(c.to);
        if (!Number.isFinite(toM)) continue;
        if (toM > fromM) continue;

        if (toM > bestToM) {
          bestToM = toM;
          best = c;
        }
      }

      if (!best) return null;

      if (String(best.location_mode || '').toLowerCase() === 'at_clinic' && best.org_location_id) {
        const hhmm = normalizeTime(best.to);
        return {
          origin_type: 'org_location',
          org_location_id: Number(best.org_location_id),
          end_time: hhmm ? `${hhmm}:00` : null
        };
      }

      return null;
    } catch {
      return null;
    }
  }

  /** ---------- Fetch eligibility ---------- */
  async function computeEligibility({ date, from, to, type, reqId }) {
    try {
      const r = await API.post('/students/eligible-for-interval', {
        teacher_id: Number(currentTeacherId),
        date,
        from,
        to,
        type,
        limit: 500,
        distance_mode: distanceMode,
        sort_by_distance: sortOthersByDistance,

        visit_location_mode: visitLocationMode,
        org_location_id: (visitLocationMode === 'at_clinic' && selectedClinic?.id) ? Number(selectedClinic.id) : null,
        origin_override: buildOriginOverrideForModalInterval(date, from),
      });

      if (reqId && reqId !== fetchRef.current) return;

      const defSrv = r.data?.default_students || [];
      const othSrv = r.data?.others || [];
      const defMap = new Map(defSrv.map(s => [s.id, s]));

      const list = [];
      for (const s of defaultStudents) {
        const serverHit = defMap.get(s.id);
        let ok = !!serverHit;
        let reason = '';
        let enriched = { ...s };

        if (serverHit) {
          const programCodes = Array.isArray(serverHit.program_codes)
            ? serverHit.program_codes
            : Array.isArray(serverHit.disciplines)
              ? serverHit.disciplines
              : [];
          const takenCodes = Array.isArray(serverHit.taken_program_codes)
            ? serverHit.taken_program_codes
            : [];

          enriched = {
            ...enriched,
            straight_line_mi: serverHit.straight_line_mi ?? null,
            route_mi: serverHit.route_mi ?? null,
            route_min: serverHit.route_min ?? null,
            prev_ref: serverHit.prev_ref ?? null,
            location_source: serverHit.location_source ?? 'primary',
            program_codes: programCodes,
            taken_program_codes: takenCodes,
            telehealth_available: !!serverHit.telehealth_available,
            telehealth_label: serverHit.telehealth_label || 'Telehealth',
          };
        } else {
          try {
            const { freeByDate, visitsByDate } = await ensureStudentWeek(s.id);
            const free = freeByDate[date] || [];
            const hasCover = free.some(x =>
              toMinutes(from) >= toMinutes(x.from) &&
              toMinutes(to)   <= toMinutes(x.to)
            );
            const visits = visitsByDate[date] || [];
            const hasVisitThatDay = visits.some(v => v.status !== 'canceled');

            if (hasVisitThatDay) reason = 'Already has a visit on this day.';
            else if (!hasCover) reason = 'Not available in this interval.';
            else reason = 'Not eligible for this slot (server rules).';
          } catch {
            reason = 'Not eligible for this slot.';
          }
        }

        list.push({ student: enriched, ok, reason });
      }

      list.sort((a,b) => (a.ok === b.ok) ? 0 : a.ok ? -1 : 1);
      if (!reqId || reqId === fetchRef.current) setEligibleStudents(list);

      const enrichedOthers = othSrv.map(o => {
        const full = getStudentFull(o.id) || {};
        const programCodes = Array.isArray(o.program_codes)
          ? o.program_codes
          : Array.isArray(o.disciplines)
            ? o.disciplines
            : [];
        const takenCodes = Array.isArray(o.taken_program_codes)
          ? o.taken_program_codes
          : [];

        return {
          ...o,
          default_teacher_id: full.default_teacher_id ?? o.default_teacher_id ?? null,
          program_codes: programCodes,
          taken_program_codes: takenCodes,
          telehealth_available: !!o.telehealth_available,
          telehealth_label: o.telehealth_label || 'Telehealth',
        };
      });
      if (!reqId || reqId === fetchRef.current) setOtherEligible(enrichedOthers);
    } catch (e) {
      if (!reqId || reqId === fetchRef.current) {
        setEligibleStudents([]);
        setOtherEligible([]);
      }
    }
  }

  /** ---------- Modal open ---------- */
  async function openModalFor(date, seg, e) {
    if (!currentTeacherId) { setStatus('Missing teacher info.'); return; }
    const track = trackRefs.current[date];
    if (!track) return;
    if (isPastDate(date)) { setStatus('Cannot add on a past day.'); return; }

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    const segFromM = toMinutes(seg.from);
    let segToM   = toMinutes(seg.to);

    const genericBase = getDisciplineDuration(null, 'standard', 'in_person');
    const genericMin = getMinMaxDuration(null, 'standard', 'in_person').min || SLOT_MIN;

    if (segToM - segFromM < genericMin) segToM = segFromM + genericMin;

    const segLeftPx  = rect.left  + ((segFromM - dayStartM) / spanM) * rect.width;
    const segRightPx = rect.left  + ((segToM   - dayStartM) / spanM) * rect.width;
    const segWidthPx = Math.max(1, segRightPx - segLeftPx);
    const localPx    = Math.max(0, Math.min(segWidthPx, clientX - segLeftPx));
    const segSpanM   = segToM - segFromM;

    let minuteAtX =
      segFromM + Math.round((localPx / segWidthPx) * (segSpanM / SNAP_MIN)) * SNAP_MIN;
    minuteAtX = Math.max(segFromM, Math.min(segToM - genericMin, minuteAtX));

    const start = minutesToHHMM(minuteAtX);
    const defaultEnd = minutesToHHMM(Math.min(minuteAtX + genericBase, segToM));

    setModalDate(date);
    setModalFrom(start);
    setModalMaxTo(seg.to);
    setModalAnchor({ from: seg.from, to: seg.to });

    setAtHomeMode();

    const reqId = ++fetchRef.current;
    setLoadingOther(true);

    await computeEligibility({
      date,
      from: start,
      to: defaultEnd,
      type: 'standard',
      reqId,
    });

    if (fetchRef.current === reqId) {
      setShowModal(true);
      setLoadingOther(false);
    }
  }

  /** ---------- Recompute eligibility on distance/sort/location/candidates changes ---------- */
  useEffect(() => {
    if (!showModal) return;
    if (!modalDate || !modalFrom || !modalMaxTo) return;

    const genericBase = getDisciplineDuration(null, 'standard', 'in_person');
    const end = minutesToHHMM(
      Math.min(
        toMinutes(modalFrom) + genericBase,
        toMinutes(modalMaxTo)
      )
    );

    const reqId = ++fetchRef.current;
    setLoadingOther(true);
    computeEligibility({
      date: modalDate,
      from: modalFrom,
      to: end,
      type: 'standard',
      reqId,
    }).finally(() => {
      if (fetchRef.current === reqId) setLoadingOther(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceMode, sortOthersByDistance, visitLocationMode, selectedClinic?.id, candidatesByDate]);

  function handleDisciplineSelect(studentId, code) {
    setSelectedDisciplines(prev => ({ ...prev, [studentId]: code }));
  }

  /** ---------- Work modal helpers ---------- */
  function parseWorkLocation(value) {
    const v = String(value || '').trim();
    if (!v) return { location_mode: null, org_location_id: null };
    if (v === 'teacher_home') return { location_mode: 'teacher_home', org_location_id: null };
    if (v === 'remote') return { location_mode: 'remote', org_location_id: null };
    if (v.startsWith('org:')) return { location_mode: 'org_location', org_location_id: Number(v.slice(4)) || null };
    return { location_mode: null, org_location_id: null };
  }

  function openWorkModal() {
    if (!modalDate || !modalFrom || !modalAnchor?.to) {
      setStatus('Missing Work details.');
      return;
    }

    const span = toMinutes(modalAnchor.to) - toMinutes(modalFrom);
    const maxM = Number.isFinite(span) && span > 0 ? Math.max(30, Math.min(240, span)) : 240;

    setWorkForm(prev => ({
      ...prev,
      planned_minutes: clamp(Number(prev.planned_minutes || 60), 30, maxM),
    }));

    if (!orgLocations.length) loadOrgLocations();

    setWorkModalOpen(true);
    setShowModal(false);
  }

  function closeWorkModal() {
    setWorkModalOpen(false);
    if (modalDate && modalFrom && modalMaxTo) setShowModal(true);
  }

  function addWorkCandidateForDay(date, from, to, w) {
    if (isPastDate(date)) {
      setStatus('Cannot add on a past day.');
      return false;
    }

    const anchorSeg = modalAnchor || { from: DAY_START, to: DAY_END };
    if (!fitsInFreeWindow(date, anchorSeg, from, to, null)) {
      setStatus('Work overlaps with existing visits/candidates.');
      return false;
    }

    const saved = (teacherBusyByDate[date] || []).filter(x => isWorkBlock(x) && x.status !== 'canceled').length;
    const cand = (candidatesByDate[date] || []).filter(x => isWorkCandidate(x)).length;
    if ((saved + cand) >= 6) {
      setStatus('Max 6 work blocks per day reached.');
      return false;
    }

    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[date] || []).slice();
      list.push({
        candidate_kind: 'work',
        from: normalizeTime(from),
        to: normalizeTime(to),
        type: 'work',
        work_type: String(w.work_type || 'other'),
        title: (w.title || '').toString().slice(0, 160) || null,
        notes: (w.notes || '').toString() || null,
        location_mode: String(w.location_mode || ''),
        org_location_id: String(w.location_mode) === 'org_location'
          ? (w.org_location_id ? Number(w.org_location_id) : null)
          : null,
      });
      cp[date] = list;
      return cp;
    });

    return true;
  }

  async function submitWork() {
    if (workSubmitting) return;
    if (!modalDate || !modalFrom || !modalAnchor?.to) {
      setStatus('Missing Work details.');
      return;
    }

    const span = toMinutes(modalAnchor.to) - toMinutes(modalFrom);
    const maxM = Number.isFinite(span) && span > 0 ? Math.max(30, Math.min(240, span)) : 240;

    const { location_value, work_type, planned_minutes, title, notes } = workForm || {};
    const { location_mode, org_location_id } = parseWorkLocation(location_value);

    if (!location_mode) { setStatus('Please select a work location.'); return; }
    if (location_mode === 'org_location' && !org_location_id) { setStatus('Please select an organization location.'); return; }

    let mins = Number(planned_minutes || 60);
    if (!Number.isFinite(mins)) mins = 60;
    mins = clamp(mins, 30, maxM);

    const from = normalizeTime(modalFrom);
    const to = addMinutes(from, mins);

    setWorkSubmitting(true);
    try {
      const ok = addWorkCandidateForDay(modalDate, from, to, { work_type, title, notes, location_mode, org_location_id });
      if (!ok) return;

      setStatus('Work candidate added.');
      setWorkModalOpen(false);
    } finally {
      setWorkSubmitting(false);
    }
  }

  /** ---------- Visit candidate add (no event) ---------- */
  function addCandidate(
    date,
    from,
    to,
    student,
    anchor = null,
    disciplineCode = null,
    deliveryMode = 'in_person',
    telehealthLink = null
  ) {
    if (!canAddVisitForStudent(date, student, disciplineCode || null)) {
      setStatus('Cannot add: this student already has a visit for this discipline on this day.');
      return;
    }

    const locMode = (visitLocationMode === 'at_clinic' && selectedClinic?.id) ? 'at_clinic' : 'at_home';
    const locId = (locMode === 'at_clinic') ? Number(selectedClinic.id) : null;

    if (locMode === 'at_clinic' && !locId) {
      setStatus('Select clinic location first (At clinic).');
      openClinicPicker();
      return;
    }

    const full = getStudentFull(student.id) || student;
    const anchorSeg = anchor || { from: DAY_START, to: DAY_END };

    if (!fitsInFreeWindow(date, anchorSeg, from, to, null)) {
      setStatus('No free slot of required length between existing visits.');
      return;
    }

    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[date] || []).slice();
      list.push({
        from,
        to,
        student: {
          id: full.id,
          name: (full.last_name || '') + ', ' + (full.first_name || ''),
          default_teacher_id: full.default_teacher_id ?? null
        },
        type: 'standard',
        readonly: false,
        anchor: anchorSeg,
        discipline_code: disciplineCode || null,
        delivery_mode: deliveryMode || 'in_person',
        telehealth_link: telehealthLink || null,
        location_mode: locMode,
        org_location_id: locId,
      });
      cp[date] = list;
      return cp;
    });
  }

  /** Select student with discipline */
  async function selectStudentWithDiscipline(
    s,
    programCode,
    deliveryMode = 'in_person',
    telehealthLink = null
  ) {
    const baseDuration = getDisciplineDuration(programCode, 'standard', deliveryMode);

    const end = minutesToHHMM(
      Math.min(toMinutes(modalFrom) + baseDuration, toMinutes(modalMaxTo))
    );

    const { freeByDate } = await ensureStudentWeek(s.id);
    const sFree = freeByDate[modalDate] || [];
    const covering = sFree.find(x =>
      toMinutes(modalFrom) >= toMinutes(x.from) &&
      toMinutes(end)       <= toMinutes(x.to)
    );
    const studentSeg = covering || { from: modalFrom, to: end };

    const aFrom = minutesToHHMM(
      Math.max(toMinutes(modalAnchor.from), toMinutes(studentSeg.from))
    );
    const aTo = minutesToHHMM(
      Math.min(toMinutes(modalAnchor.to),   toMinutes(studentSeg.to))
    );

    const reqMin = toMinutes(end) - toMinutes(modalFrom);
    if (toMinutes(aTo) - toMinutes(aFrom) < reqMin) {
      setStatus('Student is not free for required visit length in this window.');
      return;
    }

    addCandidate(
      modalDate,
      modalFrom,
      end,
      s,
      { from: aFrom, to: aTo },
      programCode || null,
      deliveryMode,
      telehealthLink
    );
    setShowModal(false);
  }

  /** ---------- Delete candidate ---------- */
  function deleteCandidate(date, idx) {
    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[date] || []).slice();
      list.splice(idx, 1);
      cp[date] = list;
      return cp;
    });
  }

  /** ---------- Refresh helper ---------- */
  async function refreshAll() {
    if (currentTeacherId && selectedWeekStart) {
      try {
        const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${currentTeacherId}`);
        const a = res.data?.availability || {};
        const e = res.data?.exceptions || {};
        const daysOff = res.data?.day_offs || [];

        const base = {};
        Object.keys(a).forEach(date => {
          const isDayOff = daysOff.includes(date);
          const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
          base[date] = { free };
        });

        const b = await API.get('/visits/teacher-week-all', {
          params: { teacher_id: currentTeacherId, week_start: selectedWeekStart }
        });
        const busy = {};
        (Object.entries(b.data || {})).forEach(([date, list]) => {
          busy[date] = (list || []).map(r => ({
            id: r.id,
            from: normalizeTime(r.from_time),
            to: normalizeTime(r.to_hhmm || r.to_time || r.to),
            student_id: r.student_id,
            student_name: r.student_name,
            type: r.type || 'standard',
            status: r.status || 'draft',
            reassigned_status: r.reassigned_status || 'none',
            discipline_code: r.discipline_code || null,
            delivery_mode: r.delivery_mode || 'in_person',
            telehealth_link: r.telehealth_link || null,
            location_mode: r.location_mode || null,
            org_location_id: r.org_location_id || null,
          }));
        });

        try {
          const w = await API.get('/teacher-week-planner', {
            params: { teacher_id: currentTeacherId, week_start: selectedWeekStart }
          });
          const byDate = w.data?.byDate || {};
          Object.keys(byDate).forEach(date => {
            const dayBusy = Array.isArray(byDate?.[date]?.busy) ? byDate[date].busy : [];
            const workItems = dayBusy.filter(x => isWorkBlock(x) || String(x.busy_as || '') === 'work' || String(x.type || '') === 'work');
            if (!workItems.length) return;
            if (!busy[date]) busy[date] = [];
            workItems.forEach(r => {
              busy[date].push({
                id: `work_${r.work_id || getWorkId(r) || r.id}`,
                kind: 'work',
                work_id: r.work_id || getWorkId(r) || null,
                from: normalizeTime(r.from),
                to: normalizeTime(r.to),
                student_id: null,
                student_name: r.title || r.student_name || r.work_type || 'Work',
                type: 'work',
                status: r.status || 'draft',
                reassigned_status: 'none',
                discipline_code: null,
                delivery_mode: null,
                telehealth_link: null,
                busy_as: 'work',
                work_type: r.work_type || 'other',
                title: r.title || null,
                notes: r.notes || null,
                location_mode: r.location_mode || null,
                org_location_id: r.org_location_id || null,
              });
            });
          });
        } catch {
          // ignore
        }

        Object.keys(base).forEach(date => {
          const free = base[date].free || [];
          const blocked = (busy[date] || [])
            .filter(x => x.status !== 'canceled')
            .map(x => ({ from: x.from, to: x.to }));
          base[date].free = subtractIntervals(free, blocked);
        });

        setTeacherBusyByDate(busy);
        setTeacherAvailByDate(base);
      } catch {
        // ignore
      }
    }
  }

  /** ---------- Cancel/Delete helpers ---------- */
  function isLockedSaved(item, date) {
    const st = String(item?.status || '');
    if (st !== 'draft') return true;
    if (isPastDate(date)) return true;
    return false;
  }
  function isCancelableSaved(item, date) {
    const st = String(item?.status || '');
    if (st === 'complete' || st === 'canceled') return false;
    if (isPastDate(date)) return false;
    return true;
  }

  async function deleteSavedItem(date, item) {
    const isWork = isWorkBlock(item);
    const id = isWork ? getWorkId(item) : getVisitId(item);

    if (!id) { setStatus(isWork ? 'Missing work id.' : 'Missing visit id.'); return; }
    if (isLockedSaved(item, date)) { setStatus(isWork ? 'This work block is locked.' : 'This visit is locked.'); return; }

    const ok = window.confirm(isWork ? 'Delete this draft work block?' : 'Delete this draft visit?');
    if (!ok) return;

    try {
      if (isWork) await API.delete(`/teacher-work/${id}`);
      else await API.delete(`/visits/${id}`);
      setStatus('Deleted.');
      await refreshAll();
    } catch (e) {
      setStatus(e?.response?.data?.error || 'Delete failed.');
    }
  }

  function openCancelModal(date, item) {
    const isWork = isWorkBlock(item);
    const id = isWork ? getWorkId(item) : getVisitId(item);

    if (!id) { setStatus(isWork ? 'Missing work id.' : 'Missing visit id.'); return; }
    if (!isCancelableSaved(item, date)) {
      setStatus(isWork ? 'This work block cannot be canceled.' : 'This visit cannot be canceled.');
      return;
    }

    setCancelTarget({ date, item });
    setCancelMode(isWork ? 'work' : 'visit');
    setCancelReason('');
    setShowCancelModal(true);
  }

  async function confirmCancel() {
    if (!cancelTarget?.date || !cancelTarget?.item) return;

    const date = cancelTarget.date;
    const item = cancelTarget.item;
    const isWork = cancelMode === 'work' || isWorkBlock(item);
    const id = isWork ? getWorkId(item) : getVisitId(item);
    const reason = String(cancelReason || '').trim();

    if (!id) { setStatus(isWork ? 'Missing work id.' : 'Missing visit id.'); return; }
    if (!reason) { setStatus(isWork ? 'Please enter a short reason.' : 'Please select a reason.'); return; }
    if (!isCancelableSaved(item, date)) { setStatus(isWork ? 'This work block cannot be canceled.' : 'This visit cannot be canceled.'); return; }

    setCancelSubmitting(true);
    try {
      if (isWork) await API.post(`/teacher-work/${id}/cancel`, { reason });
      else await API.post(`/admin/visits/${id}/cancel`, { reason });

      setStatus('Canceled.');
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelMode('visit');
      setCancelReason('');

      await refreshAll();
    } catch (e) {
      setStatus(e?.response?.data?.error || 'Cancel failed.');
    } finally {
      setCancelSubmitting(false);
    }
  }

  /** ---------- Save all candidates (visits + work) ---------- */
  async function handleSaveAll() {
    if (!currentTeacherId) {
      setStatus('Missing teacher info.');
      return;
    }

    const visitsPayload = [];
    const workPayload = [];
    const skipped = [];

    const usedDisc = new Set();
    for (const { date } of weekDates) {
      const busy = (teacherBusyByDate[date] || []);
      for (const v of busy) {
        if (v.status === 'canceled') continue;
        if (isWorkBlock(v)) continue;
        const baseKey = `${date}|${v.student_id}|${v.discipline_code || 'GEN'}`;
        usedDisc.add(baseKey);
      }
    }

    for (const { date } of weekDates) {
      if (isPastDate(date)) continue;
      const list = (candidatesByDate[date] || []);

      for (const c of list) {
        if (isWorkCandidate(c)) {
          const from = normalizeTime(c.from);
          const to = normalizeTime(c.to);
          const mins = Math.max(1, toMinutes(to) - toMinutes(from));

          workPayload.push({
            teacher_id: Number(currentTeacherId),
            scheduled_at: `${date} ${from}`,
            planned_minutes: mins,
            end_time: to,
            status: 'draft',
            work_type: String(c.work_type || 'other'),
            title: (c.title || '').toString().slice(0, 160) || null,
            notes: (c.notes || '').toString() || null,
            location_mode: String(c.location_mode || ''),
            org_location_id: String(c.location_mode) === 'org_location'
              ? (c.org_location_id ? Number(c.org_location_id) : null)
              : null,
          });
          continue;
        }

        const baseKey = `${date}|${c.student.id}|${c.discipline_code || 'GEN'}`;
        if (usedDisc.has(baseKey)) {
          skipped.push({ date, student: c.student.name, from: c.from, to: c.to });
          continue;
        }
        usedDisc.add(baseKey);

        const studentDefaultTid =
          c.student.default_teacher_id ??
          getStudentDefaultTid(c.student.id) ??
          null;

        const isDefaultForThisTeacher =
          String(studentDefaultTid || '') === String(currentTeacherId);

        const original_tid = isDefaultForThisTeacher
          ? Number(currentTeacherId)
          : (studentDefaultTid ? Number(studentDefaultTid) : Number(currentTeacherId));

        const reassigned_status = isDefaultForThisTeacher
          ? 'none'
          : (studentDefaultTid ? 'pending' : 'none');

        visitsPayload.push({
          student_id: c.student.id,
          teacher_id: Number(currentTeacherId),
          scheduled_at: `${date} ${normalizeTime(c.from)}`,
          notes: '',
          visit_id: null,
          status: 'draft',
          type: 'standard',
          event_end_time: normalizeTime(c.to),
          original_teacher_id: original_tid,
          reassigned_status,
          discipline_code: c.discipline_code || null,
          delivery_mode: c.delivery_mode || 'in_person',
          telehealth_link: c.telehealth_link || null,
          location_mode: c.location_mode || 'at_home',
          org_location_id: c.org_location_id || null,
        });
      }
    }

    if (!visitsPayload.length && !workPayload.length) {
      if (skipped.length) setStatus('Nothing to save (all candidates skipped by per-day/per-discipline limits).');
      else setStatus('Nothing to save.');
      return;
    }

    try {
      if (visitsPayload.length) {
        const resV = await API.post('/visits/bulk', { visits: visitsPayload });
        if (!resV.data?.success) {
          setStatus('Visits save failed.');
          return;
        }
      }

      if (workPayload.length) {
        const resW = await API.post('/teacher-work/bulk', { work: workPayload });
        const okW = !!resW.data?.success;
        const errs = Array.isArray(resW.data?.errors) ? resW.data.errors : [];
        if (!okW) {
          setStatus(errs[0]?.error || 'Work save failed.');
          return;
        }
      }

      if (skipped.length) setStatus(`Saved; skipped ${skipped.length} due to per-day/per-discipline limits.`);
      else setStatus('Saved.');

      setCandidatesByDate({});
      await refreshAll();
    } catch (e) {
      setStatus(e?.response?.data?.error || 'Save failed.');
    }
  }

  /** ---------- Drag & Resize za kandidate (work + discipline rules) ---------- */
  function startDrag(e, date, idx, mode) {
    const track = trackRefs.current[date];
    if (!track) return;
    const item = (candidatesByDate[date] || [])[idx];
    if (!item) return;

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    e.preventDefault();
    setDrag({
      date,
      idx,
      mode,
      startX: clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      origFrom: item.from,
      origTo: item.to,
      discipline_code: item.discipline_code || null,
      delivery_mode: item.delivery_mode || 'in_person',
      isWork: isWorkCandidate(item),
    });
  }

  function onMove(e) {
    if (!drag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = clientX - drag.rectLeft;
    const minuteAtX = dayStartM +
      Math.round((dx / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;

    setCandidatesByDate(prev => {
      const cp = { ...prev };
      const list = (cp[drag.date] || []).slice();
      const item = { ...list[drag.idx] };
      if (!item) return prev;

      const origDur = toMinutes(drag.origTo) - toMinutes(drag.origFrom);

      let minDur = SLOT_MIN;
      let maxDur = 90;
      let baseDur = getDisciplineDuration(drag.discipline_code, 'standard', drag.delivery_mode || 'in_person');
      let hardLimited = isDisciplineHardLimited(drag.discipline_code);

      if (drag.isWork) {
        minDur = 30;
        maxDur = 240;
        baseDur = Math.max(30, Math.min(240, origDur || 60));
        hardLimited = false;
      } else {
        const mm = getMinMaxDuration(drag.discipline_code, 'standard', drag.delivery_mode || 'in_person');
        minDur = mm.min || SLOT_MIN;
        maxDur = mm.max || maxDur;
      }

      let newFrom = toMinutes(item.from);
      let newTo   = toMinutes(item.to);

      if (drag.mode === 'move') {
        const anchorAtStart = dayStartM +
          Math.round(((drag.startX - drag.rectLeft) / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;
        const anchorOffset = toMinutes(drag.origFrom) - anchorAtStart;
        newFrom = minuteAtX + anchorOffset;
        newTo   = newFrom + origDur;
      } else if (drag.mode === 'resize-left') {
        const maxLeft = toMinutes(item.to) - minDur;
        newFrom = Math.min(maxLeft, minuteAtX);
      } else if (drag.mode === 'resize-right') {
        const minRight = toMinutes(item.from) + minDur;
        newTo = Math.max(minRight, minuteAtX);
      }

      newFrom = Math.max(dayStartM, Math.min(newFrom, dayEndM - minDur));
      newTo   = Math.max(newFrom + minDur, Math.min(newTo, dayEndM));
      if (isPastDate(drag.date)) return prev;

      const anchor = item.anchor || { from: DAY_START, to: DAY_END };
      const aFrom = toMinutes(anchor.from);
      const aTo   = toMinutes(anchor.to);
      if (aTo - aFrom < minDur) return prev;

      if (drag.mode === 'move') {
        let dur = newTo - newFrom;
        if (hardLimited) dur = baseDur;
        newFrom = Math.max(aFrom, Math.min(aTo - dur, newFrom));
        newTo   = newFrom + dur;
      } else {
        newFrom = Math.max(aFrom, newFrom);
        newTo   = Math.min(aTo,   newTo);
        let dur = newTo - newFrom;

        if (hardLimited) {
          dur = baseDur;
          if (drag.mode === 'resize-left') {
            newFrom = Math.max(aFrom, newTo - dur);
            newTo   = newFrom + dur;
          } else {
            newTo   = Math.min(aTo, newFrom + dur);
            newFrom = newTo - dur;
          }
        } else {
          if (dur < minDur) {
            if (drag.mode === 'resize-left') newFrom = Math.max(aFrom, newTo - minDur);
            else newTo = Math.min(aTo, newFrom + minDur);
            dur = newTo - newFrom;
          }
          if (maxDur && dur > maxDur) {
            if (drag.mode === 'resize-left') newFrom = Math.max(aFrom, newTo - maxDur);
            else newTo = Math.min(aTo, newFrom + maxDur);
          }
        }
      }

      const newFromHH = minutesToHHMM(newFrom);
      const newToHH   = minutesToHHMM(newTo);

      if (!fitsInFreeWindow(drag.date, anchor, newFromHH, newToHH, drag.idx)) return prev;

      item.from = newFromHH;
      item.to   = newToHH;

      list[drag.idx] = item;
      cp[drag.date] = list;
      return cp;
    });
  }

  function endDrag() { setDrag(null); }

  useEffect(() => {
    function up(){ endDrag(); }
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up, { passive: false });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  /** ---------- Telehealth modal helpers ---------- */
  function openTelehealthModal(student, disciplineCode) {
    if (!student || !disciplineCode) return;
    setTeleStudent(student);
    setTeleDisciplineCode(disciplineCode);
    setTeleLink('');
    setTeleError('');
    setShowModal(false);
    setShowTeleModal(true);
  }

  async function handleTelehealthSave() {
    if (!teleStudent) { setTeleError('Missing student.'); return; }
    if (!teleDisciplineCode) { setTeleError('Discipline is missing. Please close and select discipline again.'); return; }
    if (!teleLink || !teleLink.trim()) { setTeleError('Please enter telehealth link.'); return; }

    try {
      await selectStudentWithDiscipline(teleStudent, teleDisciplineCode, 'telehealth', teleLink.trim());
      setShowTeleModal(false);
      setTeleStudent(null);
      setTeleDisciplineCode(null);
      setTeleLink('');
      setTeleError('');
    } catch (e) {
      setTeleError('Failed to add telehealth visit.');
    }
  }

  function cancelTelehealthModal() {
    setShowTeleModal(false);
    setTeleStudent(null);
    setTeleDisciplineCode(null);
    setTeleLink('');
    setTeleError('');
    if (modalDate && modalFrom && modalMaxTo) setShowModal(true);
  }

  /** ---------- UI ---------- */
  return (
    <div className="p-2 max-w-7xl mx-auto">
      <Toast text={status} onClose={()=>setStatus('')} />

      <h2 className="text-lg font-bold mb-2">
        My Day Planner (1 visit per discipline per student per day)
      </h2>

      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end mb-4">
        <div>
          <div className="block mb-1 font-medium">Teacher:</div>
          <div className="border px-2 py-1 w-64 bg-gray-50 rounded">
            {currentTeacherName || 'Me'}
          </div>
        </div>

        {!!currentTeacherId && (
          <>
            <div>
              <label className="block mb-1">Week:</label>
              <select
                className="border px-2 py-1 w-56"
                value={selectedWeekStart}
                onChange={e => setSelectedWeekStart(e.target.value)}
              >
                <option value="">-- Choose week --</option>
                {weekOptions.map((w,i)=>
                  <option key={i} value={w.value}>{w.label}</option>
                )}
              </select>
            </div>

            <div>
              <label className="block mb-1">
                Default student
                <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] align-middle">
                  overlap
                </span>
              </label>

              <select
                className="border px-2 py-1 w-64"
                value={selectedStudentId}
                onChange={async e => {
                  const v = e.target.value;
                  setSelectedStudentId(v);
                  if (v) await ensureStudentWeek(Number(v));
                }}
              >
                <option value="">-- Choose student --</option>
                {defaultStudents
                  .slice()
                  .sort((a,b)=>
                    (a.last_name||'').localeCompare(b.last_name||'') ||
                    (a.first_name||'').localeCompare(b.first_name||'')
                  )
                  .map(s => (
                    <option key={s.id} value={s.id}>
                      {(s.last_name||'')+', '+(s.first_name||'')}
                    </option>
                  ))}
              </select>
            </div>
          </>
        )}
      </div>

      {!!currentTeacherId && !!selectedWeekStart && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm mb-3">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <span className="inline-block w-5 h-3 rounded bg-blue-300" /> Teacher free
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-5 h-3 rounded bg-teal-400" /> Intersection with selected student
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-5 h-3 rounded bg-gray-300 border" /> Candidate
              </span>
            </div>
            <div className="text-xs text-gray-600">
              {durationLegendText}
            </div>
          </div>

          <div className="space-y-4">
            {weekDates.map(({date, dayName}) => {
              const tFree = (teacherAvailByDate[date]?.free || [])
                .map(i => clampTo(i, dayStartM, dayEndM))
                .filter(Boolean);
              const inter = selectedStudentId
                ? (intersectionByDate[date] || [])
                : [];
              const busy = teacherBusyByDate[date] || [];
              const cands = candidatesByDate[date] || [];
              const isPastDay = isPastDate(date);

              return (
                <div key={date} className="border rounded p-3 shadow-sm">
                  <div className={`font-semibold mb-2 px-2 py-1 rounded flex items-center gap-2 ${
                    isPastDay ? 'text-gray-400 bg-gray-50' : 'text-gray-800 bg-gray-100'
                  }`}>
                    <span>{dayName} – {date}</span>
                    {!!busy.length && (
                      <span className="text-xs px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                        Saved {busy.length}
                      </span>
                    )}
                    {!!cands.length && (
                      <span className="text-xs px-1.5 py-0.5 rounded border bg-gray-50 text-gray-700 border-gray-200">
                        Candidates {cands.length}
                      </span>
                    )}
                  </div>

                  <div
                    className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                    ref={el => (trackRefs.current[date] = el)}
                  >
                    {/* hourly ticks */}
                    {Array.from({length: (spanM/60)+1}).map((_,i)=>{
                      const leftPct = (i*60)/spanM*100;
                      return (
                        <div key={i}>
                          <div
                            className="absolute top-4 bottom-0 border-l border-gray-300"
                            style={{ left: `${leftPct}%` }}
                          />
                          <div
                            className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2"
                            style={{ left: `${leftPct}%` }}
                          >
                            {minutesToHHMM(dayStartM + i*60).slice(0,2)}
                          </div>
                        </div>
                      );
                    })}

                    {/* teacher free */}
                    {tFree.map((seg,idx)=>{
                      const box = pctBox(seg);
                      const clickable = !isPastDay;
                      return clickable ? (
                        <button
                          key={'t'+idx}
                          className="absolute top-4 bottom-1 bg-blue-300 opacity-40 hover:opacity-50 rounded z-10"
                          style={{left:box.left,width:box.width}}
                          title={`Teacher free ${seg.from}–${seg.to}`}
                          onClick={(e)=>openModalFor(date, seg, e)}
                        />
                      ) : (
                        <div
                          key={'t'+idx}
                          className="absolute top-4 bottom-1 bg-blue-300 opacity-30 rounded z-10"
                          style={{left:box.left,width:box.width}}
                        />
                      );
                    })}

                    {/* intersection */}
                    {inter.map((seg,idx)=>{
                      const box = pctBox(seg);
                      const clickable = !isPastDay;
                      return clickable ? (
                        <button
                          key={'i'+idx}
                          className="absolute top-4 bottom-1 bg-teal-400/70 hover:bg-teal-400 rounded z-20"
                          style={{left:box.left,width:box.width}}
                          title={`Intersection ${seg.from}–${seg.to}`}
                          onClick={(e)=>openModalFor(date, seg, e)}
                        />
                      ) : (
                        <div
                          key={'i'+idx}
                          className="absolute top-4 bottom-1 bg-teal-400/50 rounded z-20"
                          style={{left:box.left,width:box.width}}
                        />
                      );
                    })}

                    {/* saved (visits + work) */}
                    {busy.map((s, idx)=>{
                      const box = pctBox(s);
                      const isCanceled = s.status === 'canceled';
                      const isComplete = s.status === 'complete';
                      const isWork = isWorkBlock(s);
                      const locked = isLockedSaved(s, date);

                      const containerCls = isCanceled
                        ? 'border border-gray-300 bg-gray-50 opacity-60'
                        : isComplete
                          ? 'border-gray-400 bg-gray-100'
                          : 'border-emerald-700 bg-white';

                      const fillCls = isCanceled
                        ? 'bg-gray-200 opacity-50'
                        : isComplete
                          ? 'bg-gray-300'
                          : (isWork ? 'bg-gray-300 opacity-60' : `${colorForId(s.student_id)} opacity-40`);

                      const textCls = isCanceled ? 'text-gray-500' : '';
                      const zCls = isCanceled ? 'z-5' : 'z-30';

                      const label = isWork
                        ? (s.title || s.work_type || s.student_name || 'Work')
                        : (s.student_name || '—');

                      return (
                        <div
                          key={'b'+idx}
                          className={`absolute top-5 bottom-2 rounded shadow-sm overflow-hidden ${containerCls} ${zCls}`}
                          style={{left:box.left,width:box.width}}
                        >
                          <div className={`h-full ${fillCls}`} />
                          <div className={`absolute inset-0 flex items-center justify-between px-2 text-xs gap-1 ${textCls}`}>
                            <span className="font-mono">{s.from}–{s.to}</span>
                            <span className="hidden sm:inline font-semibold">{label}</span>
                            <span className="sm:hidden font-semibold">{initials(label)}</span>

                            {isWork ? (
                              <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
                                Work
                              </span>
                            ) : (
                              <DisciplineBadge code={s.discipline_code} />
                            )}
                            <TelehealthPill mode={s.delivery_mode} />
                            <StatusBadge status={s.status} />
                            {!isWork ? <ReassignedBadge value={s.reassigned_status} /> : null}
                          </div>

                          {!isPastDay && !locked && (
                            <button
                              className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                              title="Delete draft"
                              onClick={() => deleteSavedItem(date, s)}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* candidates (visits + work) */}
                    {cands.map((c, idx)=>{
                      const box = pctBox(c);
                      const isWork = isWorkCandidate(c);
                      const label = isWork ? (c.title || c.work_type || 'Work') : (c.student?.name || '—');

                      return (
                        <div
                          key={'c'+idx}
                          className="absolute top-5 bottom-2 rounded border border-gray-400 bg-white shadow-sm overflow-hidden z-40"
                          style={{left:box.left,width:box.width}}
                        >
                          <div className={`${isWork ? 'bg-gray-300' : colorForId(c.student.id)} h-full opacity-40`} />
                          <div className="absolute inset-0 flex items-center justify-between px-2 text-xs gap-1">
                            <span className="font-mono">{c.from}–{c.to}</span>
                            <span className="hidden sm:inline font-semibold">{label}</span>
                            <span className="sm:hidden font-semibold">{initials(label)}</span>

                            {isWork ? (
                              <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
                                Work
                              </span>
                            ) : (
                              <>
                                <DisciplineBadge code={c.discipline_code} />
                                <TelehealthPill mode={c.delivery_mode} />
                              </>
                            )}
                          </div>

                          {!isPastDay && (
                            <>
                              <div
                                className="absolute inset-0 cursor-grab"
                                onMouseDown={(e)=>startDrag(e,date,idx,'move')}
                                onTouchStart={(e)=>startDrag(e,date,idx,'move')}
                              />
                              <div
                                className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize"
                                onMouseDown={(e)=>startDrag(e,date,idx,'resize-left')}
                                onTouchStart={(e)=>startDrag(e,date,idx,'resize-left')}
                              />
                              <div
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize"
                                onMouseDown={(e)=>startDrag(e,date,idx,'resize-right')}
                                onTouchStart={(e)=>startDrag(e,date,idx,'resize-right')}
                              />
                            </>
                          )}

                          <button
                            className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                            title="Remove"
                            onClick={() => deleteCandidate(date, idx)}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {(busy.length > 0 || cands.length > 0) && (
                    <div className="mt-2 space-y-1">
                      {busy.map((s, idx) => {
                        const isWork = isWorkBlock(s);
                        const locked = isLockedSaved(s, date);
                        const cancelable = isCancelableSaved(s, date);

                        const showClinic = isClinicLikeItem(s, isWork);
                        const clinicLabel = showClinic ? resolveClinicLabel(s.org_location_id) : null;

                        return (
                          <div key={'bl'+idx} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`inline-block w-3 h-3 rounded-full ${
                                  s.status === 'canceled'
                                    ? 'bg-gray-300'
                                    : (isWork ? 'bg-gray-400' : colorForId(s.student_id))
                                }`}
                              />
                              <span className={`font-semibold ${s.status === 'canceled' ? 'text-gray-500' : ''}`}>
                                {isWork ? (s.title || s.work_type || s.student_name || 'Work') : (s.student_name || '—')}
                              </span>
                              <span className={`font-mono ${s.status === 'canceled' ? 'text-gray-500' : ''}`}>
                                {s.from}–{s.to}
                              </span>

                              {showClinic ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 text-[11px] max-w-[260px]"
                                  title={clinicLabel || 'Clinic'}
                                >
                                  <Building2 size={14} />
                                  <span className="truncate">{clinicLabel || 'Clinic'}</span>
                                </span>
                              ) : null}

                              {isWork ? (
                                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
                                  Work
                                </span>
                              ) : (
                                <DisciplineBadge code={s.discipline_code} />
                              )}
                              <TelehealthPill mode={s.delivery_mode} />
                              <StatusBadge status={s.status} />
                              {!isWork ? <ReassignedBadge value={s.reassigned_status} /> : null}
                            </div>

                            <div className="flex items-center gap-2">
                              {locked && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                                  <FaLock />
                                  Locked
                                </span>
                              )}

                              <button
                                type="button"
                                className={
                                  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ' +
                                  (cancelable
                                    ? 'border-purple-200 text-purple-700 hover:bg-purple-50'
                                    : 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50')
                                }
                                disabled={!cancelable}
                                onClick={() => openCancelModal(date, s)}
                                title={cancelable ? 'Cancel' : 'Not cancelable'}
                              >
                                <FaBan />
                                Cancel
                              </button>

                              <button
                                type="button"
                                className={
                                  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ' +
                                  (locked
                                    ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                                    : 'border-red-200 text-red-700 hover:bg-red-50')
                                }
                                disabled={locked}
                                onClick={() => deleteSavedItem(date, s)}
                                title={locked ? 'Locked' : 'Delete draft'}
                              >
                                <FaTrash />
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {cands.map((c, idx) => {
                        const isWork = isWorkCandidate(c);
                        const showClinic = isClinicLikeItem(c, isWork);
                        const clinicLabel = showClinic ? resolveClinicLabel(c.org_location_id) : null;

                        return (
                          <div key={'cl'+idx} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-block w-3 h-3 rounded-full ${isWork ? 'bg-gray-400' : colorForId(c.student.id)}`} />
                              <span className="font-semibold">
                                {isWork ? (c.title || c.work_type || 'Work') : (c.student.name || '—')}
                              </span>
                              <span className="font-mono">{c.from}–{c.to}</span>

                              {showClinic ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 text-[11px] max-w-[260px]"
                                  title={clinicLabel || 'Clinic'}
                                >
                                  <Building2 size={14} />
                                  <span className="truncate">{clinicLabel || 'Clinic'}</span>
                                </span>
                              ) : null}

                              {isWork ? (
                                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
                                  Work
                                </span>
                              ) : (
                                <>
                                  <DisciplineBadge code={c.discipline_code} />
                                  <TelehealthPill mode={c.delivery_mode} />
                                  <span className="text-xs text-gray-600">candidate</span>
                                </>
                              )}
                            </div>
                            <button
                              className="px-2 py-0.5 rounded border text-red-600"
                              onClick={() => deleteCandidate(date, idx)}
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            <button
              onClick={handleSaveAll}
              className="px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700"
            >
              Save all candidates
            </button>
          </div>
        </>
      )}

      {/* Modal: Select Student */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2000]">
          <div className="bg-white p-4 rounded max-w-xl w-full relative max-h-[80vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={()=>setShowModal(false)}
            >
              ×
            </button>
            <h3 className="text-lg font-bold mb-2">Select Student</h3>
            <div className="text-sm text-gray-600 mb-2">
              Date: <b>{modalDate}</b> | Start: <b>{modalFrom}</b>
            </div>

            {/* Distance controls */}
            <div className="flex flex-wrap items-center gap-3 mb-3 bg-gray-50 rounded p-2">
              <label className="text-sm">
                Distance mode:{' '}
                <select
                  className="border px-2 py-1 text-sm"
                  value={distanceMode}
                  onChange={e => setDistanceMode(e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="haversine">Straight (Haversine)</option>
                  <option value="ors">Route (ORS)</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sortOthersByDistance}
                  onChange={e => setSortOthersByDistance(e.target.checked)}
                />
                <span>Sort others by distance</span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-1 text-xs text-gray-500 mt-1 mb-2">
              {durationLegendSpans}
            </div>

            {/* Work slot */}
            <div className="mb-3 border rounded p-2 bg-slate-50">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold">Work</div>
                  <div className="text-[11px] text-gray-600">
                    Paperwork/admin task for this teacher (30–240 min). Max 6 per day.
                  </div>
                </div>

                <button
                  className="px-3 py-1.5 rounded text-sm border bg-white hover:bg-gray-50"
                  onClick={openWorkModal}
                  title="Add work block"
                >
                  Add Work
                </button>
              </div>
            </div>

            {/* Visit location mode: At home / At clinic */}
            <div className="mb-2 flex items-center justify-end gap-2">
              <button
                type="button"
                className={
                  'px-2 py-0.5 rounded text-xs border ' +
                  (visitLocationMode === 'at_home'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                }
                onClick={() => setAtHomeMode()}
                title="At home"
              >
                At home
              </button>

              <button
                className={
                  'px-2 py-0.5 rounded text-xs border ' +
                  (visitLocationMode === 'at_clinic'
                    ? 'bg-yellow-50 text-orange-700 border-orange-300 hover:bg-yellow-100'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                }
                onClick={openClinicPicker}
                type="button"
                title="At clinic (pick location)"
              >
                At clinic
              </button>

              {visitLocationMode === 'at_clinic' ? (
                selectedClinic ? (
                  <span className="text-[11px] text-gray-500">
                    Clinic: <b>{selectedClinic.name || `#${selectedClinic.id}`}</b>
                  </span>
                ) : (
                  <span className="text-[11px] text-red-600">
                    Clinic not selected
                  </span>
                )
              ) : null}
            </div>

            <div className="mb-2 text-xs text-gray-600">
              First select a <b>discipline</b> for each student using the colored buttons (EI, PI, ABA, OT, PT, ST).{' '}
              Then click <b>In person</b> or <b>Telehealth</b> to create a visit for that discipline.
            </div>

            {/* Default students */}
            {eligibleStudents.length ? (
              eligibleStudents.map(row => {
                const stu = row.student || {};
                const hasPrograms = Array.isArray(stu.program_codes) && stu.program_codes.length > 0;
                const canTelehealth = !!stu.telehealth_available;
                const selectedDisc = selectedDisciplines[stu.id];

                return (
                  <div
                    key={stu.id}
                    className={`mb-2 border p-2 rounded ${row.ok ? 'bg-green-50' : 'bg-yellow-50'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-3 h-3 rounded-full ${colorForId(stu.id)}`} />
                          <div className="font-semibold">
                            {(stu.last_name||'')+', '+(stu.first_name||'')}
                            <PrevRefPill prev_ref={stu.prev_ref} />
                          </div>
                        </div>
                        <DistanceLine item={stu} />
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {hasPrograms ? (
                          <DisciplineButtonsRow
                            student={stu}
                            isEligible={row.ok}
                            selectedCode={selectedDisc}
                            onSelectDiscipline={(code) => row.ok && handleDisciplineSelect(stu.id, code)}
                          />
                        ) : null}

                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (row.ok && selectedDisc
                                ? 'bg-green-600 text-white border-green-700 hover:bg-green-700'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!row.ok || !selectedDisc}
                            onClick={() => row.ok && selectedDisc && selectStudentWithDiscipline(stu, selectedDisc, 'in_person', null)}
                          >
                            In person
                          </button>

                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (row.ok && canTelehealth && selectedDisc
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!row.ok || !canTelehealth || !selectedDisc}
                            onClick={() => row.ok && canTelehealth && selectedDisc && openTelehealthModal(stu, selectedDisc)}
                          >
                            <span className="inline-flex items-center gap-1">
                              <span className="text-xs">📹</span>
                              <span>{stu.telehealth_label || 'Telehealth'}</span>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {!row.ok && row.reason && (
                      <div className="text-xs text-gray-700 mt-1">
                        {row.reason}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-gray-700">
                No eligible default students for this time window.
              </div>
            )}

            <hr className="my-3" />
            <div className="text-sm font-semibold mb-2">Other students (available now)</div>

            {loadingOther ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : otherEligible.length ? (
              otherEligible.map(s => {
                const hasPrograms = Array.isArray(s.program_codes) && s.program_codes.length > 0;
                const canTelehealth = !!s.telehealth_available;
                const selectedDisc = selectedDisciplines[s.id];

                return (
                  <div key={s.id} className="mb-2 border p-2 rounded bg-blue-50">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-3 h-3 rounded-full ${colorForId(s.id)}`} />
                          <div className="font-semibold">
                            {(s.last_name||'')+', '+(s.first_name||'')}
                            <PrevRefPill prev_ref={s.prev_ref} />
                          </div>
                        </div>
                        <DistanceLine item={s} />
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {hasPrograms ? (
                          <DisciplineButtonsRow
                            student={s}
                            isEligible={true}
                            selectedCode={selectedDisc}
                            onSelectDiscipline={(code) => handleDisciplineSelect(s.id, code)}
                          />
                        ) : null}

                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (selectedDisc
                                ? 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!selectedDisc}
                            onClick={() => selectedDisc && selectStudentWithDiscipline(s, selectedDisc, 'in_person', null)}
                          >
                            In person
                          </button>

                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (canTelehealth && selectedDisc
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!canTelehealth || !selectedDisc}
                            onClick={() => canTelehealth && selectedDisc && openTelehealthModal(s, selectedDisc)}
                          >
                            <span className="inline-flex items-center gap-1">
                              <span className="text-xs">📹</span>
                              <span>{s.telehealth_label || 'Telehealth'}</span>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-gray-600">
                No other available students for this window.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clinic picker modal */}
      {clinicModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3175]">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5 relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={() => setClinicModalOpen(false)}
              title="Close"
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">Select clinic location</h3>

            <div className="text-sm text-gray-600 mb-3">
              Choose an organization location (required for at_clinic).
            </div>

            <label className="block text-sm font-medium mb-1">Clinic location</label>
            <select
              className="w-full border rounded px-2 py-2 text-sm bg-white"
              value={selectedClinic ? String(selectedClinic.id) : ''}
              onChange={(e) => {
                const id = Number(e.target.value || 0);
                const found = (orgLocations || []).find(x => Number(x.id) === id);
                if (found) setSelectedClinic({ id: found.id, name: found.name, geocoded_label: found.geocoded_label });
                else setSelectedClinic(null);
              }}
              disabled={orgLocationsLoading}
            >
              <option value="">{orgLocationsLoading ? 'Loading…' : 'Select clinic…'}</option>
              {(orgLocations || []).map((ol) => (
                <option key={ol.id} value={ol.id}>
                  {(ol.name || 'Location') + (ol.geocoded_label ? ` — ${ol.geocoded_label}` : '')}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100"
                onClick={() => setClinicModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                onClick={() => {
                  if (!selectedClinic) return;
                  setVisitLocationMode('at_clinic');
                  setClinicModalOpen(false);
                }}
                disabled={!selectedClinic}
              >
                Use clinic
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Work modal */}
      {workModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3300]">
          <div className="bg-white p-4 rounded max-w-md w-full relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={closeWorkModal}
              disabled={workSubmitting}
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">Add Work Block</h3>

            <div className="text-sm text-gray-700 mb-3">
              Date: <b>{modalDate}</b> | Start: <b>{modalFrom}</b>
              {modalAnchor?.to ? (
                <span> | Window to: <b>{modalAnchor.to}</b></span>
              ) : null}
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Location</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                value={workForm.location_value || ''}
                onChange={(e) => setWorkForm(prev => ({ ...prev, location_value: e.target.value }))}
                disabled={orgLocationsLoading || workSubmitting}
              >
                <option value="">{orgLocationsLoading ? 'Loading…' : 'Select location'}</option>

                {orgLocations.map(loc => (
                  <option key={loc.id} value={`org:${loc.id}`}>
                    {`Org: ${loc.name || 'Location'}${loc.city ? ` (${loc.city})` : ''}`}
                  </option>
                ))}

                <option value="teacher_home">Teacher home</option>
                <option value="remote">Remote</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Work type</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                value={workForm.work_type || 'admin'}
                onChange={(e) => setWorkForm(prev => ({ ...prev, work_type: e.target.value }))}
                disabled={workSubmitting}
              >
                <option value="admin">admin</option>
                <option value="documentation">documentation</option>
                <option value="training">training</option>
                <option value="research">research</option>
                <option value="meeting">meeting</option>
                <option value="other">other</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Minutes</label>
              <input
                type="number"
                min={30}
                max={240}
                value={workForm.planned_minutes ?? 60}
                onChange={(e) => setWorkForm(prev => ({ ...prev, planned_minutes: Number(e.target.value || 60) }))}
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                disabled={workSubmitting}
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Saved only on “Save all candidates”.
              </div>
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Title (optional)</label>
              <input
                type="text"
                value={workForm.title || ''}
                onChange={(e) => setWorkForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                maxLength={160}
                disabled={workSubmitting}
                placeholder="e.g., Paperwork"
              />
            </div>

            <div className="mb-4">
              <label className="text-sm font-semibold">Notes (optional)</label>
              <textarea
                value={workForm.notes || ''}
                onChange={(e) => setWorkForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                rows={3}
                disabled={workSubmitting}
                placeholder="Optional notes…"
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm"
                onClick={closeWorkModal}
                disabled={workSubmitting}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-2 rounded text-sm text-white ${
                  workSubmitting ? 'bg-slate-400' : 'bg-sky-700 hover:bg-sky-800'
                }`}
                onClick={submitWork}
                disabled={workSubmitting}
              >
                {workSubmitting ? 'Saving…' : 'Add Work'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3250]">
          <div className="bg-white p-4 rounded max-w-md w-full relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={() => {
                if (cancelSubmitting) return;
                setShowCancelModal(false);
                setCancelTarget(null);
                setCancelMode('visit');
                setCancelReason('');
              }}
              title="Close"
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">
              {cancelMode === 'work' ? 'Cancel work block' : 'Cancel visit'}
            </h3>

            <div className="text-sm text-gray-600 mb-3">
              {cancelMode === 'work'
                ? 'Please enter a short reason (required).'
                : 'Please select a reason (required).'}
            </div>

            {cancelMode === 'work' ? (
              <>
                <label className="block text-xs text-gray-600 mb-1">
                  Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={cancelSubmitting}
                  placeholder="e.g. Schedule changed — work task moved to another time."
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {WORK_CANCEL_QUICK_REASONS.map((txt) => (
                    <button
                      key={txt}
                      type="button"
                      className="text-[11px] px-2 py-1 rounded border bg-slate-50 hover:bg-slate-100"
                      onClick={() => setCancelReason(txt)}
                      disabled={cancelSubmitting}
                    >
                      {txt}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <label className="block text-xs text-gray-600 mb-1">
                  Cancel reason <span className="text-red-600">*</span>
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  disabled={cancelSubmitting}
                >
                  <option value="">Select a reason…</option>
                  {CANCEL_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded border hover:bg-gray-50 disabled:opacity-60"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelTarget(null);
                  setCancelMode('visit');
                  setCancelReason('');
                }}
                disabled={cancelSubmitting}
              >
                Back
              </button>

              <button
                type="button"
                className="px-3 py-2 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                onClick={confirmCancel}
                disabled={!String(cancelReason || '').trim() || cancelSubmitting}
              >
                {cancelSubmitting ? 'Canceling…' : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telehealth link modal */}
      {showTeleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2100]">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5 relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={cancelTelehealthModal}
            >
              ×
            </button>
            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
              <span className="text-xl">📹</span>
              <span>Telehealth link</span>
            </h3>
            <div className="text-sm text-gray-700 mb-2">
              Student:{' '}
              <b>
                {teleStudent
                  ? `${teleStudent.last_name || ''}, ${teleStudent.first_name || ''}`
                  : '—'}
              </b>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Date: <b>{modalDate}</b> | Start: <b>{modalFrom}</b> | Window to:{' '}
              <b>{modalMaxTo}</b>
            </div>
            <label className="block text-sm font-medium mb-1">
              Telehealth URL (Zoom, Teams, Google Meet…)
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={teleLink}
              onChange={e => {
                setTeleLink(e.target.value);
                if (teleError) setTeleError('');
              }}
              placeholder="https://..."
            />
            {teleError && (
              <div className="text-xs text-red-600 mt-1">{teleError}</div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100"
                onClick={cancelTelehealthModal}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={handleTelehealthSave}
              >
                Add Telehealth visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}









